'use client';

import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import {
  ShieldCheck, Lock, Server, FileCheck, AlertTriangle, Award, Eye, Database,
  Globe, ArrowRight, CheckCircle, Building2,
} from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { WHITEPAPER_CONTENT } from '@/core/i18n/content/whitepaper.content';

const SECTION_ICONS = [FileCheck, Server, Lock, Eye, Database, AlertTriangle, Award];

export function SecurityWhitepaperPage() {
  const c = usePageContent(WHITEPAPER_CONTENT);

  return (
    <div className="min-h-screen bg-hms-bg">
      {/* Hero */}
      <section id="swp-hero" className="relative py-20 sm:py-28 px-5 md:px-10">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hms-gold/10 border border-hms-gold/20 mb-6">
            <ShieldCheck className="w-4 h-4 text-hms-gold" />
            <span className="text-[13px] font-medium text-hms-gold">{c.hero.badge}</span>
          </div>
          <h1 id="swp-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-hms-text leading-tight mb-5">{c.hero.title}</h1>
          <p id="swp-subheading" className="text-hms-textSub text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-8">{c.hero.subtitle}</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-[12px] text-hms-textSub">
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-hms-surface border border-hms-border/60"><Globe className="w-3.5 h-3.5 text-hms-teal" /> {c.hero.stats.countries}</span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-hms-surface border border-hms-border/60"><Building2 className="w-3.5 h-3.5 text-hms-teal" /> {c.hero.stats.properties}</span>
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-hms-surface border border-hms-border/60"><ShieldCheck className="w-3.5 h-3.5 text-hms-green" /> {c.hero.stats.breaches}</span>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section id="swp-toc" className="py-10 px-5 md:px-10 border-t border-hms-border/40">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-lg font-bold text-hms-text mb-4">{c.toc}</h2>
          <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {c.sections.map((section, idx) => {
              const Icon = SECTION_ICONS[idx] ?? FileCheck;
              return (
                <Link key={idx} href={`#swp-section-${idx}`} id={`swp-toc-link-${idx}`} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] text-hms-textSub hover:text-hms-text hover:bg-hms-surface transition-colors">
                  <Icon className="w-4 h-4 text-hms-gold flex-shrink-0" />
                  <span>{idx + 1}. {section.title}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </section>

      {/* Sections */}
      {c.sections.map((section, idx) => {
        const Icon = SECTION_ICONS[idx] ?? FileCheck;
        return (
          <section key={idx} id={`swp-section-${idx}`} className="py-12 px-5 md:px-10 border-t border-hms-border/40">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-hms-gold/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-hms-gold" />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-hms-textDim uppercase tracking-wider">{c.sectionLabel} {idx + 1}</p>
                  <h2 className="text-xl sm:text-2xl font-bold text-hms-text">{section.title}</h2>
                </div>
              </div>
              <div className="space-y-4 mb-6">
                {section.content.map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-hms-textSub text-[14px] leading-relaxed">{paragraph}</p>
                ))}
              </div>
              {section.details && (
                <div className="p-5 rounded-xl bg-hms-surface border border-hms-border/60">
                  <p className="text-[12px] font-bold text-hms-textDim uppercase tracking-wider mb-3">{c.keyControls}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {section.details.map((detail) => (
                      <li key={detail} className="flex items-center gap-2 text-[13px] text-hms-textSub">
                        <CheckCircle className="w-3.5 h-3.5 text-hms-green flex-shrink-0" />{detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section id="swp-cta" className="py-16 px-5 md:px-10 border-t border-hms-border/40">
        <div className="max-w-3xl mx-auto text-center">
          <ShieldCheck className="w-10 h-10 text-hms-gold mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-hms-text mb-4">{c.cta.title}</h2>
          <p className="text-hms-textSub text-[14px] max-w-lg mx-auto mb-8">{c.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <LocaleLink id="swp-cta-contact" href="/contact" className="px-6 py-3 rounded-lg bg-gradient-to-r from-hms-gold to-hms-goldLt text-black font-semibold text-[13px] hover:from-hms-goldLt hover:to-hms-gold transition-all shadow-md shadow-hms-gold/25 flex items-center gap-2">
              {c.cta.contact}<ArrowRight className="w-4 h-4" />
            </LocaleLink>
            <LocaleLink id="swp-cta-security" href="/security" className="px-6 py-3 rounded-lg border border-hms-border text-hms-textSub hover:text-hms-text hover:border-hms-borderLt text-[13px] font-medium transition-colors">{c.cta.security}</LocaleLink>
          </div>
        </div>
      </section>
    </div>
  );
}
